import { Address, LocalizedString } from '../../types'

type LoanOfferMonthlyTotals = {
  fees: number
  instalments: number
  instalmentsIncreased: number
  interests: number
  interestsIncreased: number
  paymentProtectionFees?: number
  payments: number
  paymentsIncreased: number
}

type LoanOfferSavings = {
  monthly: number
  savingsFor3Years: number
  savingsFor10Years: number
  savingsForLongestLoan: number
}

export type LoanOfferApplicant = {
  name: string
  monthlyTotals: LoanOfferMonthlyTotals
  savings: LoanOfferSavings
}

export type LoanOfferProtection = {
  description: LocalizedString
  type: LoanProtectionType
}

export type LoanOfferReasonMetadata = {
  offeredLoanId: string
  loanType: LoanOfferType
  isLoanTransfer: boolean
}

export type LoanOfferReason = LoanOfferReasonMetadata & {
  loanType: LoanOfferType
  loanPurpose?: LoanOfferPurpose
  amount: number
  targetType?: LoanOfferTargetType
  targetAddress?: Address
  isLoanTransfer: boolean
  financingType: LoanOfferFinancingType
  amortization: LoanOfferAmortization
  firstPaymentAmount: number
  installmentAmount: number
  installmentInterestAmount: number
  paymentFrequency?: LoanOfferFrequencyInMonths
  interestFrequency?: LoanOfferFrequencyInMonths
  loanPeriodMonths: number
  openingFee: number
  feesRelatedToOfficialFees?: number
  installmentFreePeriodMonths: number
  debitFee: number
  baseRateType: LoanOfferBaseRate
  baseRateValue: number
  baseRateDate: string
  baseRateAfterFixed?: LoanOfferNonFixedBaseRate
  margin: number
  totalInterestRate: number
  effectiveAnnualInterestRate: number
  interestCalculationType?: LoanOfferInterestCalculationType
  protections: LoanOfferProtection[]
  coDebtorName?: string
}

export interface OfferCondition {
  description: string
}

export enum LoanProtectionType {
  A = 'A', // 'Henki-Fennia'
  B = 'B', // 'Nykyinen Axa'
  AB = 'AB', // 'Nykyinen laaja, Henki-Fennia ja nykyinen Axa'
  AB2 = 'AB2', // 'Uusi laaja, Henki-Fennia ja Axa 2.0'
  B2 = 'B2', // taken into use in near future, and will deprecate 'B' and 'AB'
}

export type Banker = {
  name: string
  phoneNumber?: string
}

export type LoanOfferMetadata = {
  id: string
  state: LoanOfferState
  updated: number
  offeredLoans: LoanOfferReasonMetadata[]
}

export type LoanOfferItem = LoanOfferMetadata & {
  loanOfferMode: LoanOfferMode
  sHousingGuaranteeFee?: number
  applicant: LoanOfferApplicant
  expiration: LoanOfferExpiration
  offeredLoans: LoanOfferReason[]
  offerConditions?: OfferCondition[]
  banker?: Banker
  reservationLink?: string
}

export type LoanOfferExpiration = {
  pricingValidUntilDate: string
  termsValidUntilDate: string
}

export enum LoanOfferMode {
  Automatic = 'automatic',
  Manual = 'manual',
}

export enum LoanOfferAmortization {
  Annuity = 'annuity',
  ConstantAmount = 'constantAmount',
  ConstantDuration = 'constantDuration',
  Bullet = 'bullet',
}

export enum LoanOfferType {
  HousingLoan = 'housingLoan',
  CottageLoan = 'cottageLoan',
  InvestmentLoan = 'investmentLoan',
  RenovationLoan = 'renovationLoan',
  ConstructionLoan = 'constructionLoan',
  ConsumptionLoan = 'consumptionLoan',
}

export enum LoanOfferPurpose {
  Car = 'carPurpose',
  Consumption = 'consumptionPurpose',
  Investment = 'investmentPurpose',
  ObjectRelated = 'objectRelatedPurpose',
  Other = 'otherPurpose',
}

export enum LoanOfferFinancingType {
  Regular = 'regular',
  Bridging = 'bridging',
  Asp = 'asp',
}

export enum LoanOfferFixedBaseRate {
  Fixed3 = 'fixed3',
  Fixed5 = 'fixed5',
  Fixed10 = 'fixed10',
}

export enum LoanOfferNonFixedBaseRate {
  Euribor1 = 'euribor1',
  Euribor3 = 'euribor3',
  Euribor6 = 'euribor6',
  Euribor12 = 'euribor12',
  Prime = 'prime',
}

export const LoanOfferBaseRate = {
  ...LoanOfferFixedBaseRate,
  ...LoanOfferNonFixedBaseRate,
} as const

export type LoanOfferBaseRate = (typeof LoanOfferBaseRate)[keyof typeof LoanOfferBaseRate]

export enum LoanOfferInterestCalculationType {
  CT_360_360 = 'interestCalculationType_360_360',
  CT_365_360 = 'interestCalculationType_365_360',
  CT_365_365 = 'interestCalculationType_365_365',
}

export enum LoanOfferTargetType {
  HousingCooperative = 'housingCooperative',
  RealEstate = 'realEstate',
  RightOfOccupancy = 'rightOfOccupancy',
  PartOwnership = 'partOwnership',
  HolidayShare = 'holidayShare',
  Timeshare = 'timeshare',
  RightToRent = 'rightToRent',
  BasicImprovement = 'basicImprovement',
  Unknown = 'unknown',
}

export enum LoanOfferState {
  OfferActive = 'OfferActive',
  PricingExpired = 'PricingExpired',
  TermsExpired = 'OfferExpired',
}

export interface LoanOfferIdPathParam {
  loanOfferId: string
}

export enum LoanOfferDocumentType {
  Esis = 'esis',
  AmortizationTable = 'amortizationTable',
}

export interface GetLoanOfferDocumentQueryParams {
  documentType: LoanOfferDocumentType
  offeredLoanId: string
}

export type LoanOfferGetLoanOfferResponse = LoanOfferItem

export enum LoanOfferFrequencyInMonths {
  Monthly = 1,
  Quarterly = 3,
  HalfYearly = 6,
  Yearly = 12,
}
